import React, { useState } from 'react';

import { useGetFace, useGetFaceAttributeSamples, useMatchFaces } from '@vlabs/api/luna-api/client';
import type { FaceCandidateMatchResult, FaceMatchResult } from '@vlabs/api/luna-api/model';
import { checkCard, putCard } from '@vlabs/api/point-api/client';
import { FolderPlusIcon } from '@vlabs/icons';
import { Control, useModal } from '@vlabs/uikit';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { toast } from 'react-toastify';

import { AttachFaceToCard } from '@vlabs/monokit/features/attach-face-to-card';
import { AttachSimilarFaceToCard } from '@vlabs/monokit/features/attach-similar-face-to-card';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import st from './AttachFaceToCardButton.module.sass';
import { useAttachFaceToCardButtonTranslation } from './i18n';
import type { AttachFaceToCardProps, State } from './types';
import { CreateCardForm, createCardFromFaceFormSubmitHandler } from '../../entities/cards/create-card';

interface AttachFaceToCardButtonProps extends AttachFaceToCardProps, ButtonProps, WithDataTestid {}

const displayName = 'AttachFaceToCardButton';
const testid = buildTestid(displayName);

function AttachFaceToCardButton({
  faceId,
  onAttachFaceToCard = undefined,
  'data-testid': dataTestId = undefined,
  children,
  ...props
}: AttachFaceToCardButtonProps): React.JSX.Element {
  const { t } = useAttachFaceToCardButtonTranslation();
  const getFaceLists = useGetFace(faceId, { targets: ['lists'] });
  const getFaceAttributeSamples = useGetFaceAttributeSamples(faceId);
  const [modalContent, setModalContent] = useState<State>();
  const [similarFace, setSimilarFace] = useState<Required<FaceCandidateMatchResult>>();
  const modal = useModal(modalContent === 'create' ? { size: 'large' } : { size: 'small' });
  const matchFaces = useMatchFaces({
    mutation: {
      onSuccess: async (response) => {
        const matches = ((response)?.[0]?.matches?.[0] as FaceMatchResult | undefined)?.result;

        const match = matches
          ?.filter((faceResult) => faceResult?.face?.external_id)
          ?.sort(({ similarity: a }, { similarity: b }) => (b as number) - (a as number))[0];

        if (match?.face?.external_id) {
          try {
            await checkCard(match.face.external_id);
          } catch {
            await putCard(match.face.external_id, { main_face_id: match.face.face_id });
          }
        }

        setSimilarFace(match as Required<FaceCandidateMatchResult> | undefined);

        if (match?.similarity && match?.face?.external_id) {
          setModalContent('similar');
        } else {
          setModalContent('attach');
        }

        modal.open();
      },
      onError: () => {
        toast.error(t('toast_error'));
      },
    },
  });

  return (
    <React.Fragment>
      {modal.wrap(
        <React.Fragment>
          {modalContent === 'similar' && !!similarFace && (
            <React.Fragment>
              <AttachSimilarFaceToCard
                faceId={faceId}
                onSuccess={() => {
                  modal.close();
                  onAttachFaceToCard?.({ fromState: modalContent, cardId: similarFace.face.external_id as string });
                }}
                similarityFace={similarFace}
              />
              <div className={st.Actions}>
                <Control.Button
                  className={st.ButtonWrapper}
                  onClick={() => setModalContent('attach')}
                  variant="outlined"
                >
                  {t('button_another')}
                </Control.Button>

                <Control.Button
                  className={st.ButtonWrapper}
                  kind="secondary"
                  onClick={modal.close}
                  variant="outlined"
                >
                  {t('button_cancel')}
                </Control.Button>
              </div>
            </React.Fragment>
          )}

          {modalContent === 'attach' && (
            <React.Fragment>
              <AttachFaceToCard
                faceId={faceId}
                onSuccess={(cardId) => {
                  modal.close();
                  onAttachFaceToCard?.({ fromState: modalContent, cardId });
                }}
              />
              <div className={st.Actions}>
                <Control.Button
                  className={st.ButtonWrapper}
                  onClick={() => setModalContent('create')}
                  variant="outlined"
                >
                  {t('button_create')}
                </Control.Button>

                <Control.Button
                  className={st.ButtonWrapper}
                  kind="secondary"
                  onClick={modal.close}
                  variant="outlined"
                >
                  {t('button_cancel')}
                </Control.Button>
              </div>
            </React.Fragment>
          )}

          {modalContent === 'create' && (
            <CreateCardForm
              defaultValues={{
                lists: getFaceLists.data?.lists,
                sampleId: getFaceAttributeSamples?.data?.samples?.[0],
              }}
              onCancel={modal.close}
              onSubmit={async ({ sampleId, ...values }) => {
                const cardId = await createCardFromFaceFormSubmitHandler({ ...values, faceId });
                if (cardId) onAttachFaceToCard?.({ fromState: modalContent, cardId });
                modal.close();
              }}
            />
          )}
        </React.Fragment>,
      )}

      <Control.Button
        icon={<FolderPlusIcon />}
        kind="secondary"
        onClick={() => matchFaces.mutate({
          data: {
            candidates: [{
              filters: { origin: 'faces' },
              targets: ['external_id', 'avatar', 'similarity', 'face_id'],
              threshold: 0.8,
            }],
            references: [{
              type: 'face',
              id: faceId,
            }],
          },
        })}
        variant="filled"
        {...testid(dataTestId)}
        {...props}
      >
        {children ?? t('button_add')}
      </Control.Button>
    </React.Fragment>
  );
}

AttachFaceToCardButton.displayName = displayName;

export { AttachFaceToCardButton };
